import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import YhTable from "../../ui/YhTable";
import YhInput from "../../ui/YhInput";
import YhSelect from "../../ui/YhSelect";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import notifiers from "../../global/constants/NotificationConstants";
import urls from "../../global/constants/UrlConstants";
import YhButton from "../../ui/YhButton";
import {
  selectActiveOrganizationId,
  selectIsSuperAdmin,
  selectPrivileges,
  selectRole1,
} from "../../redux/authSlice";
import {
  canSendPaymentLinks,
  canViewPayments,
} from "../../Shared/AppDrawer/menuPrivileges";
import { listTenantOrganizations } from "../../services/organizationService";
import YhModal from "../../ui/YhModal";
import {
  getPaymentList,
  sendPaymentLink,
  sendPaymentLinksBulk,
  syncEasebuzzTransactions,
} from "./PaymentService";

const SearchSvg = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const statusBadge = (status: string) => {
  const s = String(status || "").toLowerCase();
  if (s === "paid" || s === "success" || s === "successful") {
    return "bg-[#DCFCE7] text-[#166534]";
  }
  if (s === "pending" || s === "pre-initiated" || s === "preinitiated") {
    return "bg-[#FEF3C7] text-[#92400E]";
  }
  if (s === "unpaid") {
    return "bg-[#F9F0F4] text-[#3730A3]";
  }
  if (s === "bounced" || s === "dropped" || s === "cancelled") {
    return "bg-[#DBEAFE] text-[#1E40AF]";
  }
  if (s === "failure" || s === "failed") {
    return "bg-[#FEE2E2] text-[#991B1B]";
  }
  return "bg-[#F3F4F6] text-[#4B5563]";
};

const statusLabel = (row: any) => {
  const raw = String(row?.easebuzzStatus || row?.paymentStatus || "").trim();
  if (!raw) return "—";
  const s = raw.toLowerCase();
  if (s === "paid" || s === "success" || s === "successful") return "Success";
  if (s === "failed" || s === "failure") return "Failure";
  if (s === "bounced") return "Bounced";
  if (s === "dropped") return "Dropped";
  if (s === "pending") return "Pending";
  if (s === "pre-initiated" || s === "preinitiated") return "Pre-Initiated";
  if (s === "unpaid") return "Unpaid";
  if (s === "cancelled") return "Cancelled";
  return raw;
};

/** "" when no payment attempt exists yet, otherwise "test" | "prod". */
const rowMode = (row: any): "" | "test" | "prod" => {
  const s = String(row?.env || "").trim().toLowerCase();
  if (!s) return "";
  return ["prod", "production", "live"].includes(s) ? "prod" : "test";
};

const toTitleCase = (value: string) => {
  const s = String(value || "").trim().toLowerCase();
  if (!s) return "";
  return s.replace(/\b\w/g, (ch) => ch.toUpperCase());
};

const PaymentsPage = () => {
  const history = useHistory();
  const activeOrgId = useSelector(selectActiveOrganizationId);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const hierarchyLevel = useSelector(selectRole1);
  const canSelectOrganization =
    isSuperAdmin || String(hierarchyLevel) === "1";

  // Sending a link charges a customer, so it needs an explicit grant rather
  // than riding along with page access. Mirrors requirePrivilege on
  // POST /payment/send-link.
  const privileges = useSelector(selectPrivileges) || {};
  const canAccessPayments = canViewPayments(privileges);
  const canSendLinks = canSendPaymentLinks(privileges);

  const lockedOrgId =
    !canSelectOrganization &&
    activeOrgId &&
    activeOrgId !== "all" &&
    activeOrgId !== "*"
      ? String(activeOrgId)
      : "";

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [envFilter, setEnvFilter] = useState("all");
  const [orgFilter, setOrgFilter] = useState(lockedOrgId || "");
  const [orgs, setOrgs] = useState<any[]>([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [metaData, setMetaData] = useState<any>({});
  const [counts, setCounts] = useState({
    total: 0,
    paid: 0,
    failed: 0,
    pending: 0,
    unpaid: 0,
    test: 0,
  });
  const [syncing, setSyncing] = useState(false);
  const loadSeqRef = React.useRef(0);

  // Send-link flow: confirm recipient + amount before minting a fresh link.
  const [sendTarget, setSendTarget] = useState<any>(null);
  const [sendEmail, setSendEmail] = useState("");
  const [sendAmount, setSendAmount] = useState("");
  const [sending, setSending] = useState(false);
  /** Organization's configured Easebuzz amount, from the Integrations screen. */
  const [defaultAmount, setDefaultAmount] = useState<number | null>(null);

  // Bulk send
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkAmount, setBulkAmount] = useState("");
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResults, setBulkResults] = useState<any[] | null>(null);

  // Keep page org filter aligned with header org switcher.
  useEffect(() => {
    if (
      activeOrgId &&
      activeOrgId !== "all" &&
      activeOrgId !== "*"
    ) {
      setOrgFilter(String(activeOrgId));
    } else if (activeOrgId === "all" || activeOrgId === "*") {
      setOrgFilter("");
    }
  }, [activeOrgId]);

  // Debounce search so "Vishal Tiwari" does not get overwritten by older keystroke responses
  useEffect(() => {
    const t = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
    }, 350);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (!canSelectOrganization) return;
    (async () => {
      try {
        const res = await listTenantOrganizations();
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : [];
        setOrgs(list);
      } catch (_) {
        setOrgs([]);
      }
    })();
  }, [canSelectOrganization]);

  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    try {
      setLoading(true);
      const effectiveOrg = lockedOrgId || orgFilter || "";
      const res = await getPaymentList({
        pageNo: paginationModel.page,
        pageSize: paginationModel.pageSize,
        search,
        status: statusFilter,
        env: envFilter,
        organizationId: effectiveOrg || undefined,
      });
      if (seq !== loadSeqRef.current) return;
      setRows(Array.isArray(res?.data) ? res.data : []);
      setMetaData(res?.metadata?.[0] || {});
      setDefaultAmount(
        Number(res?.defaultAmount) > 0 ? Number(res.defaultAmount) : null
      );
      setCounts({
        total: res?.counts?.total ?? res?.metadata?.[0]?.total ?? 0,
        paid: res?.counts?.paid ?? 0,
        failed: res?.counts?.failed ?? 0,
        pending: res?.counts?.pending ?? 0,
        unpaid: res?.counts?.unpaid ?? 0,
        test: res?.counts?.test ?? 0,
      });
    } catch (error: any) {
      if (seq !== loadSeqRef.current) return;
      openErrorNotification(
        isTruthy(error?.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    search,
    statusFilter,
    envFilter,
    orgFilter,
    lockedOrgId,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const res = await syncEasebuzzTransactions();
      openSuccessNotification(
        res?.message || `Synced ${res?.imported || 0} payments`
      );
      await load();
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error?.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setSyncing(false);
    }
  };

  /** Paid rows are excluded from selection — re-sending would charge again. */
  const isSelectable = (row: any) => String(row?.paymentStatus) !== "paid";

  const selectableIds = useMemo(
    () =>
      (rows || [])
        .filter(isSelectable)
        .map((r: any) => String(r?._id || ""))
        .filter(Boolean),
    [rows]
  );

  const allPageSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selectedIds.includes(id));

  const toggleSelect = (id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked
        ? prev.includes(id)
          ? prev
          : [...prev, id]
        : prev.filter((x) => x !== id)
    );
  };

  const toggleSelectAllPage = (checked: boolean) => {
    setSelectedIds((prev) =>
      checked
        ? Array.from(new Set([...prev, ...selectableIds]))
        : prev.filter((id) => !selectableIds.includes(id))
    );
  };

  const selectedRows = useMemo(
    () => (rows || []).filter((r: any) => selectedIds.includes(String(r._id))),
    [rows, selectedIds]
  );

  // Rows with no email can't be mailed — call it out before sending.
  const selectedWithEmail = useMemo(
    () => selectedRows.filter((r: any) => String(r.leadEmail || "").trim()),
    [selectedRows]
  );

  const openBulkDialog = () => {
    setBulkResults(null);
    setBulkAmount("");
    setBulkOpen(true);
  };

  const closeBulkDialog = () => {
    setBulkOpen(false);
    setBulkResults(null);
    setBulkAmount("");
  };

  const handleBulkSend = async () => {
    if (!selectedWithEmail.length) {
      openErrorNotification("None of the selected rows have an email address");
      return;
    }
    const amountTrimmed = bulkAmount.trim();
    if (amountTrimmed) {
      const n = Number(amountTrimmed);
      if (!Number.isFinite(n) || n <= 0) {
        openErrorNotification("Enter a valid amount, or leave it blank");
        return;
      }
    }
    try {
      setBulkSending(true);
      const res = await sendPaymentLinksBulk({
        items: selectedWithEmail.map((r: any) => ({
          paymentId: r.paymentId || undefined,
          leadId: r.leadId || undefined,
          email: r.leadEmail || undefined,
        })),
        amount: amountTrimmed || undefined,
        organizationId: lockedOrgId || orgFilter || undefined,
      });
      if (res?.success) {
        setBulkResults(Array.isArray(res.results) ? res.results : []);
        if (res.failed) {
          openErrorNotification(res.message);
        } else {
          openSuccessNotification(res.message);
          setSelectedIds([]);
        }
        await load();
      } else {
        openErrorNotification(res?.message || notifiers.GENERIC_ERROR);
      }
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error?.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setBulkSending(false);
    }
  };

  const handleCopyLink = async (row: any) => {
    const link = String(row?.paymentUrl || "");
    if (!link) {
      openErrorNotification(
        "No payment link yet for this record. Use “Send link” to create one."
      );
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      openSuccessNotification("Payment link copied");
    } catch (_) {
      openErrorNotification("Unable to copy link");
    }
  };

  const openSendDialog = (row: any) => {
    setSendTarget(row);
    setSendEmail(String(row?.leadEmail || ""));
    // Re-sends repeat the previous amount; first-time links use the amount
    // configured for this organization.
    setSendAmount(
      row?.amount
        ? String(Number(row.amount))
        : defaultAmount
          ? String(defaultAmount)
          : ""
    );
  };

  const closeSendDialog = () => {
    setSendTarget(null);
    setSendEmail("");
    setSendAmount("");
  };

  const handleSendLink = async () => {
    if (!sendTarget) return;
    const email = sendEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      openErrorNotification("Enter a valid email address");
      return;
    }
    const amountNum = Number(sendAmount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      openErrorNotification("Enter a valid amount");
      return;
    }
    try {
      setSending(true);
      const res = await sendPaymentLink({
        paymentId: sendTarget.paymentId || undefined,
        leadId: sendTarget.leadId || undefined,
        email,
        amount: amountNum,
        organizationId: lockedOrgId || orgFilter || undefined,
      });
      if (res?.success) {
        // A fresh link is minted per send, so surface it for immediate copying.
        if (res.paymentUrl) {
          try {
            await navigator.clipboard.writeText(res.paymentUrl);
          } catch (_) {
            /* clipboard is best-effort here */
          }
        }
        if (res.emailed === false) {
          openErrorNotification(res.message || "Email failed — link copied");
        } else {
          openSuccessNotification(res.message || "Payment link sent");
        }
        closeSendDialog();
        await load();
      } else {
        openErrorNotification(res?.message || notifiers.GENERIC_ERROR);
      }
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error?.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setSending(false);
    }
  };

  const orgOptions = useMemo(
    () => [
      { label: "All organizations", value: "" },
      ...orgs.map((o) => ({
        label: o.brandName || o.name,
        value: String(o._id),
      })),
    ],
    [orgs]
  );

  const columns = useMemo(
    () => [
      ...(canSendLinks
        ? [
            {
              field: "_select",
              headerName: (
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[#A02448]"
                  checked={allPageSelected}
                  onChange={(e) => toggleSelectAllPage(e.target.checked)}
                  aria-label="Select all sendable rows on this page"
                  title="Select all rows on this page that can receive a link"
                />
              ),
              width: 52,
              minWidth: 52,
              renderCell: ({ row }: any) => {
                if (!isSelectable(row)) {
                  return (
                    <span
                      className="text-[10px] text-[#9CA3AF]"
                      title="Already paid"
                    >
                      —
                    </span>
                  );
                }
                const id = String(row._id || "");
                return (
                  <input
                    type="checkbox"
                    data-action-button="true"
                    className="h-4 w-4 accent-[#A02448]"
                    checked={selectedIds.includes(id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleSelect(id, e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Select payment"
                  />
                );
              },
            },
          ]
        : []),
      {
        field: "easebuzzId",
        headerName: "Easebuzz ID",
        flex: 1.1,
        minWidth: 150,
        renderCell: ({ row }: any) => (
          <span className="font-mono text-[14px] font-semibold text-[#111827]">
            {row.easebuzzId || "—"}
          </span>
        ),
      },
      {
        field: "leadName",
        headerName: "Lead",
        flex: 1.2,
        minWidth: 160,
        renderCell: ({ row }: any) => (
          <button
            type="button"
            className="text-left"
            onClick={() => {
              if (row.leadId) {
                const leadPayload = {
                  _id: row.leadId,
                  leadName: row.leadName || "",
                  leadEmail: row.leadEmail || "",
                  leadPhone: row.leadPhone || "",
                  uid: row.leadUid || "",
                  organizationId: row.organizationId || "",
                  source: row.source || "",
                  stage: row.callStatus || "",
                };
                try {
                  sessionStorage.setItem(
                    `all-lead-details-${row.leadId}`,
                    JSON.stringify(leadPayload)
                  );
                } catch {
                  // ignore
                }
                history.push(
                  urls.ALLLEADDETAILS.replace(":id", row.leadId),
                  { lead: leadPayload }
                );
              }
            }}
          >
            <div className="font-semibold text-[#2563EB]">
              {row.leadName ? toTitleCase(row.leadName) : "—"}
            </div>
            {row.leadUid ? (
              <div className="text-[11px] text-[#6B7280]">{row.leadUid}</div>
            ) : null}
          </button>
        ),
      },
      {
        field: "leadContact",
        headerName: "Email / Phone",
        flex: 1.3,
        minWidth: 180,
        renderCell: ({ row }: any) => (
          <div className="leading-tight">
            <div className="text-sm text-[#374151]">{row.leadEmail || "—"}</div>
            <div className="text-xs text-[#6B7280]">{row.leadPhone || "—"}</div>
          </div>
        ),
      },
      {
        field: "aadhaar",
        headerName: "Aadhaar",
        flex: 1.1,
        minWidth: 150,
        renderCell: ({ row }: any) => {
          const aadhaarDigits = String(row.aadhaar || "").replace(/\D/g, "");
          const aadhaarLabel =
            aadhaarDigits.length === 12
              ? aadhaarDigits.replace(/(\d{4})(?=\d)/g, "$1 ")
              : row.aadhaarLast4
                ? `XXXX XXXX ${row.aadhaarLast4}`
                : "—";
          return (
            <span className="text-sm font-medium text-[#111827]">
              {aadhaarLabel}
            </span>
          );
        },
      },
      {
        field: "amount",
        headerName: "Amount",
        flex: 0.7,
        minWidth: 100,
        renderCell: ({ row }: any) => (
          <span className="text-sm font-semibold text-[#111827]">
            {row.amount
              ? `₹${Number(row.amount).toLocaleString("en-IN")}`
              : "—"}
          </span>
        ),
      },
      {
        field: "organizationName",
        headerName: "Organization",
        flex: 1,
        minWidth: 140,
        renderCell: ({ row }: any) => (
          <span className="text-sm text-[#374151]">
            {row.organizationName || "—"}
          </span>
        ),
      },
      {
        field: "createdAt",
        headerName: "Date",
        flex: 0.9,
        minWidth: 130,
        renderCell: ({ row }: any) => (
          <span className="text-xs text-[#6B7280]">
            {row.createdAt
              ? new Date(row.createdAt).toLocaleString("en-IN")
              : "—"}
          </span>
        ),
      },
      {
        field: "paymentStatus",
        headerName: "Status",
        flex: 0.9,
        minWidth: 120,
        renderCell: ({ row }: any) => (
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold ${statusBadge(
              row.easebuzzStatus || row.paymentStatus
            )}`}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />
            {statusLabel(row)}
          </span>
        ),
      },
      {
        field: "env",
        headerName: "Mode",
        flex: 0.6,
        minWidth: 100,
        renderCell: ({ row }: any) => {
          const mode = rowMode(row);
          if (!mode) {
            return <span className="text-xs text-[#9CA3AF]">—</span>;
          }
          return (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${
                mode === "test"
                  ? "bg-[#FEF3C7] text-[#92400E]"
                  : "bg-[#DCFCE7] text-[#166534]"
              }`}
            >
              {mode === "test" ? "Test" : "Live"}
            </span>
          );
        },
      },
      {
        field: "actions",
        headerName: "Payment Link",
        flex: 1.1,
        minWidth: 210,
        renderCell: ({ row }: any) => {
          const isPaid = String(row.paymentStatus) === "paid";
          return (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                data-action-button="true"
                disabled={!row.paymentUrl}
                title={row.paymentUrl || "No link generated yet"}
                className="action-button inline-flex items-center gap-1 rounded-lg border border-[#E5E7EB] bg-white px-2.5 py-1.5 text-xs font-semibold text-[#374151] transition hover:border-[#D4B8C0] hover:bg-[#F8FAFC] disabled:cursor-not-allowed disabled:opacity-40"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyLink(row);
                }}
              >
                Copy link
              </button>
              <button
                type="button"
                data-action-button="true"
                disabled={isPaid || !canSendLinks}
                title={
                  !canSendLinks
                    ? "You do not have permission to send payment links"
                    : isPaid
                      ? "Already paid — re-sending would charge again"
                      : "Email a payment link for the same amount"
                }
                className="action-button inline-flex items-center gap-1 rounded-lg bg-[#1E3A5F] px-2.5 py-1.5 text-xs font-semibold text-white transition hover:bg-[#152C49] disabled:cursor-not-allowed disabled:opacity-40"
                onClick={(e) => {
                  e.stopPropagation();
                  openSendDialog(row);
                }}
              >
                {Number(row.linkSentCount) > 0 ? "Resend" : "Send link"}
              </button>
              {Number(row.linkSentCount) > 0 ? (
                <span
                  className="text-[10px] font-medium text-[#6B7280]"
                  title={
                    row.linkSentAt
                      ? `Last sent ${new Date(row.linkSentAt).toLocaleString("en-IN")}`
                      : ""
                  }
                >
                  ×{row.linkSentCount}
                </span>
              ) : null}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      history,
      lockedOrgId,
      orgFilter,
      canSendLinks,
      selectedIds,
      allPageSelected,
      selectableIds,
    ]
  );

  if (!canAccessPayments) {
    return (
      <div className="flex h-[calc(100vh-68px)] items-center justify-center bg-white p-6">
        <div className="max-w-md rounded-2xl border border-[#EEF0F5] bg-white p-8 text-center shadow-sm">
          <h1 className="text-xl font-bold text-[#111827]">Payments</h1>
          <p className="mt-2 text-sm text-[#6B7280]">
            You do not have permission to view payments. Ask an admin to grant
            Payment access from Team → Access.
          </p>
          <YhButton className="mt-6" onClick={() => history.push(urls.dashboardViewPath)}>
            Back to dashboard
          </YhButton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] flex-col overflow-hidden bg-white p-4 sm:p-5 lg:p-6">
      <div className="mb-4 shrink-0">
        <h1 className="text-2xl font-bold tracking-tight text-[#111827]">
          Payments
        </h1>
        <p className="mt-1 text-sm text-[#6B7280]">
          Lead payments from website / Easebuzz — Success, Failure, Bounced,
          Dropped, Pending.
        </p>
      </div>

      <div className="mb-4 grid shrink-0 grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <div className="rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-[#6B7280]">Total</p>
          <p className="mt-1 text-2xl font-bold text-[#111827]">
            {counts.total}
          </p>
        </div>
        <div className="rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-[#6B7280]">Paid / Success</p>
          <p className="mt-1 text-2xl font-bold text-[#166534]">{counts.paid}</p>
        </div>
        <div className="rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-[#6B7280]">
            Failed / Bounced
          </p>
          <p className="mt-1 text-2xl font-bold text-[#991B1B]">
            {counts.failed}
          </p>
        </div>
        <div className="rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-[#6B7280]">Pending</p>
          <p className="mt-1 text-2xl font-bold text-[#92400E]">
            {counts.pending}
          </p>
        </div>
        <div className="rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-[#6B7280]">Unpaid</p>
          <p className="mt-1 text-2xl font-bold text-[#3730A3]">
            {counts.unpaid}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEnvFilter((prev) => (prev === "test" ? "all" : "test"));
            setPaginationModel((p) => ({ ...p, page: 0 }));
          }}
          className={`rounded-2xl border bg-white p-4 text-left shadow-sm transition hover:border-[#D4B8C0] ${
            envFilter === "test"
              ? "border-[#92400E] ring-1 ring-[#92400E]"
              : "border-[#EEF0F5]"
          }`}
          title="Show only test-mode payment attempts"
        >
          <p className="text-xs font-medium text-[#6B7280]">Test payments</p>
          <p className="mt-1 text-2xl font-bold text-[#92400E]">
            {counts.test}
          </p>
        </button>
      </div>

      <div className="mb-3 flex shrink-0 flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1 sm:max-w-[360px]">
          <YhInput
            placeholder="Search name, email, phone, Easebuzz ID…"
            value={searchInput}
            type="search"
            onChange={(e) => {
              setSearchInput(e.target.value);
            }}
            startAdornment={<SearchSvg />}
            containerClassName="w-full"
          />
        </div>
        <div className="w-full sm:w-[180px]">
          <YhSelect
            label="Payment status"
            value={statusFilter}
            onChange={(e: any) => {
              setStatusFilter(String(e?.target?.value || "all"));
              setPaginationModel((p) => ({ ...p, page: 0 }));
            }}
            options={[
              { label: "All", value: "all" },
              { label: "Success", value: "paid" },
              { label: "Failure / Bounced / Dropped", value: "failed" },
              { label: "Pending / Pre-Initiated", value: "pending" },
              { label: "Unpaid", value: "unpaid" },
            ]}
          />
        </div>
        <div className="w-full sm:w-[160px]">
          <YhSelect
            label="Mode"
            value={envFilter}
            onChange={(e: any) => {
              setEnvFilter(String(e?.target?.value || "all"));
              setPaginationModel((p) => ({ ...p, page: 0 }));
            }}
            options={[
              { label: "All modes", value: "all" },
              { label: "Test only", value: "test" },
              { label: "Live only", value: "prod" },
            ]}
          />
        </div>
        {canSelectOrganization ? (
          <div className="w-full sm:w-[220px]">
            <YhSelect
              label="Organization"
              value={orgFilter}
              onChange={(e: any) => {
                setOrgFilter(String(e?.target?.value || ""));
                setPaginationModel((p) => ({ ...p, page: 0 }));
              }}
              options={orgOptions}
            />
          </div>
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          {canSendLinks ? (
            <YhButton
              type="button"
              onClick={openBulkDialog}
              disabled={!selectedIds.length}
              className="!bg-[#A02448] !text-white"
            >
              Send payment links ({selectedIds.length})
            </YhButton>
          ) : null}
          <YhButton
            type="button"
            onClick={handleSync}
            disabled={syncing}
            className="!bg-[#1E3A5F] !text-white"
          >
            {syncing ? "Syncing…" : "Sync Easebuzz"}
          </YhButton>
        </div>
      </div>

      {selectedIds.length ? (
        <div className="mb-3 flex shrink-0 flex-wrap items-center gap-3 rounded-xl border border-[#D4B8C0] bg-[#F9F0F4] px-3 py-2 text-sm">
          <span className="font-semibold text-[#A02448]">
            {selectedIds.length} selected
          </span>
          {selectedWithEmail.length !== selectedIds.length ? (
            <span className="text-xs text-[#92400E]">
              {selectedIds.length - selectedWithEmail.length} have no email and
              will be skipped
            </span>
          ) : null}
          <button
            type="button"
            className="ml-auto text-xs font-semibold text-[#6B7280] underline"
            onClick={() => setSelectedIds([])}
          >
            Clear selection
          </button>
        </div>
      ) : null}

      <div className="relative min-h-0 flex-1">
        <YhTable
          columns={columns}
          rows={rows}
          getRowId={(row) => row._id}
          rowHeight={64}
          loading={loading}
          emptyMessage="No payments yet"
          className="h-full min-h-[280px]"
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          totalRows={metaData?.total || 0}
          onPageChange={(page) =>
            setPaginationModel((prev) => ({ ...prev, page }))
          }
          onPageSizeChange={(pageSize) =>
            setPaginationModel({ page: 0, pageSize })
          }
        />
      </div>

      <YhModal
        open={!!sendTarget}
        onClose={closeSendDialog}
        title="Send payment link"
        width={520}
        footer={
          <div className="flex justify-end gap-2">
            <YhButton
              type="button"
              onClick={closeSendDialog}
              className="!border !border-[#E5E7EB] !bg-white !text-[#374151]"
            >
              Cancel
            </YhButton>
            <YhButton
              type="button"
              onClick={handleSendLink}
              disabled={sending}
              className="!bg-[#A02448] !text-white"
            >
              {sending ? "Sending…" : "Send email"}
            </YhButton>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="rounded-xl bg-[#F8FAFC] p-3 text-sm text-[#374151]">
            <div className="font-semibold text-[#111827]">
              {sendTarget?.leadName || "—"}
            </div>
            <div className="mt-0.5 text-xs text-[#6B7280]">
              {sendTarget?.leadPhone || "No phone"}
              {sendTarget?.txnid ? ` · Last txn ${sendTarget.txnid}` : ""}
            </div>
            {rowMode(sendTarget) === "test" ? (
              <div className="mt-2 rounded-lg bg-[#FEF3C7] px-2.5 py-1.5 text-xs font-medium text-[#92400E]">
                Their last attempt was in Test mode. The new link uses whichever
                mode this organization's Easebuzz integration is set to now.
              </div>
            ) : null}
          </div>

          <YhInput
            label="Send to email"
            value={sendEmail}
            onChange={(e) => setSendEmail(e.target.value)}
            placeholder="customer@example.com"
            containerClassName="w-full"
          />

          <div>
            <YhInput
              label="Amount (₹)"
              type="number"
              value={sendAmount}
              onChange={(e) => setSendAmount(e.target.value)}
              placeholder={defaultAmount ? String(defaultAmount) : "Amount"}
              containerClassName="w-full"
            />
            <p className="mt-1 text-xs text-[#6B7280]">
              {sendTarget?.amount
                ? "Pre-filled with the amount from their previous attempt. Change it only if the customer should pay a different amount."
                : "Pre-filled with this organization's default amount. Change it only if the customer should pay a different amount."}
            </p>
          </div>

          <p className="text-xs text-[#6B7280]">
            A fresh payment link is generated each time (gateway links are
            single-use) and copied to your clipboard as a backup.
          </p>
        </div>
      </YhModal>

      <YhModal
        open={bulkOpen}
        onClose={closeBulkDialog}
        title={
          bulkResults ? "Bulk send results" : "Send payment links in bulk"
        }
        width={560}
        footer={
          <div className="flex justify-end gap-2">
            <YhButton
              type="button"
              onClick={closeBulkDialog}
              className="!border !border-[#E5E7EB] !bg-white !text-[#374151]"
            >
              {bulkResults ? "Close" : "Cancel"}
            </YhButton>
            {!bulkResults ? (
              <YhButton
                type="button"
                onClick={handleBulkSend}
                disabled={bulkSending || !selectedWithEmail.length}
                className="!bg-[#A02448] !text-white"
              >
                {bulkSending
                  ? "Sending…"
                  : `Send to ${selectedWithEmail.length}`}
              </YhButton>
            ) : null}
          </div>
        }
      >
        {bulkResults ? (
          <div className="space-y-2">
            <div className="max-h-[320px] overflow-auto rounded-xl border border-[#E5E7EB]">
              {bulkResults.map((r: any, i: number) => (
                <div
                  key={`${r.paymentId || r.email}-${i}`}
                  className="flex items-start justify-between gap-3 border-b border-[#F3F4F6] px-3 py-2 last:border-0"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-[#111827]">
                      {r.name || r.email || "—"}
                    </div>
                    <div className="truncate text-xs text-[#6B7280]">
                      {r.email}
                    </div>
                  </div>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold ${
                      r.ok
                        ? "bg-[#DCFCE7] text-[#166534]"
                        : "bg-[#FEE2E2] text-[#991B1B]"
                    }`}
                    title={r.message}
                  >
                    {r.ok ? "Sent" : "Failed"}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#6B7280]">
              Hover a Failed row to see why. Links that were created but failed
              to email can still be copied from the table.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-[#374151]">
              A fresh payment link will be generated and emailed to{" "}
              <strong>{selectedWithEmail.length}</strong> customer
              {selectedWithEmail.length === 1 ? "" : "s"}.
            </p>

            <div>
              <YhInput
                label="Amount for all (₹) — optional"
                type="number"
                value={bulkAmount}
                onChange={(e) => setBulkAmount(e.target.value)}
                placeholder="Leave blank to keep each customer's own amount"
                containerClassName="w-full"
              />
              <p className="mt-1 text-xs text-[#6B7280]">
                Leave this blank and every customer is asked for the same amount
                they attempted before
                {defaultAmount
                  ? `, or ₹${defaultAmount.toLocaleString("en-IN")} if they have no previous attempt`
                  : ""}
                . Fill it in only to charge everyone the same new amount.
              </p>
            </div>

            <div className="max-h-[200px] overflow-auto rounded-xl border border-[#E5E7EB]">
              {selectedWithEmail.map((r: any) => (
                <div
                  key={String(r._id)}
                  className="flex items-center justify-between gap-3 border-b border-[#F3F4F6] px-3 py-2 text-sm last:border-0"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium text-[#111827]">
                      {r.leadName || "—"}
                    </div>
                    <div className="truncate text-xs text-[#6B7280]">
                      {r.leadEmail}
                    </div>
                  </div>
                  <span className="shrink-0 text-xs font-semibold text-[#374151]">
                    {bulkAmount.trim()
                      ? `₹${Number(bulkAmount).toLocaleString("en-IN")}`
                      : r.amount
                        ? `₹${Number(r.amount).toLocaleString("en-IN")}`
                        : defaultAmount
                          ? `₹${defaultAmount.toLocaleString("en-IN")}`
                          : "default"}
                  </span>
                </div>
              ))}
            </div>

            <p className="text-xs text-[#92400E]">
              Paid rows can't be selected, so nobody is charged twice. Links use
              this organization's current Easebuzz mode (Test or Live).
            </p>
          </div>
        )}
      </YhModal>
    </div>
  );
};

export default PaymentsPage;
